import { AlertTriangle, Layers, UserX, Crosshair } from "lucide-react";

const TYPE_META = {
  span_of_control: { label: "Span of Control", icon: AlertTriangle },
  excessive_layers: { label: "Excessive Layers", icon: Layers },
  single_point_of_failure: { label: "Single Point of Failure", icon: UserX },
};

const fmtQar = (n) => `QAR ${Number(n).toLocaleString()}`;

export default function DiagnosticsPanel({ data, onViewChart }) {
  if (!data) return null;
  const m = data.metrics;
  const stats = [
    ["Headcount", m.headcount],
    ...(m.monthly_cost != null ? [["Monthly cost", fmtQar(m.monthly_cost)]] : []),
    ["Max depth", `${m.max_depth} layers`],
    ["Avg span", m.avg_span],
    ["Max span", m.max_span],
    ["Findings", m.findings_total],
  ];

  return (
    <div className="space-y-5" data-testid="diagnostics-panel">
      <div className="grid grid-cols-3 lg:grid-cols-6 border border-border bg-white divide-x divide-border" data-testid="diagnostics-metrics">
        {stats.map(([label, value]) => (
          <div key={label} className="px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">{label}</p>
            <p className="text-[17px] font-bold text-[var(--brand-primary)] mt-0.5">{value}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {data.findings.map((f) => {
          const meta = TYPE_META[f.type] || { label: f.type, icon: AlertTriangle };
          const Icon = meta.icon;
          return (
            <div
              key={f.id}
              data-testid={`diagnostics-finding-${f.id}`}
              className={`bg-white border border-border border-l-[3px] p-5 ${f.severity === "high" ? "border-l-red-500" : "border-l-amber-500"}`}
            >
              <div className="flex items-center gap-2.5 flex-wrap">
                <Icon className={`h-4 w-4 ${f.severity === "high" ? "text-red-600" : "text-amber-600"}`} />
                <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 ${f.severity === "high" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                  {f.severity}
                </span>
                <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-600">{meta.label}</span>
                <span className="text-[10px] font-mono text-muted-foreground ml-auto">{f.affected_employee_ids.length} affected</span>
                <button
                  data-testid={`view-on-chart-${f.id}`}
                  onClick={() => onViewChart?.(f.affected_employee_ids)}
                  className="flex items-center gap-1 text-[11px] text-teal-700 hover:text-teal-900 border border-teal-600/40 px-2 py-1 hover:bg-teal-50"
                >
                  <Crosshair className="h-3 w-3" /> View on chart
                </button>
              </div>
              <h3 className="text-[14px] font-bold text-[var(--brand-primary)] mt-2.5">{f.title}</h3>
              <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1.5">{f.impact}</p>
              <p className="text-[12.5px] leading-relaxed mt-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-teal-800 mr-2">Recommendation</span>
                <span className="text-slate-700">{f.recommendation}</span>
              </p>
            </div>
          );
        })}
        {!data.findings.length && (
          <p className="text-[13px] text-muted-foreground border border-border bg-white p-6">No structural findings detected.</p>
        )}
      </div>
    </div>
  );
}
